import { BasePlaceholder } from '../base';
import elements from '../../elements';
import { fabric } from 'fabric';

export enum ShapeEnum {
  CIRCLE = 'circle',
  RECTANGLE = 'square',
}

export enum OrientationEnum {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export interface FoulsIndicatorsProps {
  maxCount: number;
  color: string;
  spacing: number;
  shape: ShapeEnum;
  width: number;
  height: number;
  orientation: OrientationEnum;
}

class TeamPeriodFouls extends BasePlaceholder {
  /**
   * Создает индикаторы фолов в виде кругов или квадратов
   * @param props Параметры для создания индикаторов
   * @returns Массив созданных объектов fabric
   */
  static createFoulsIndicators(props: FoulsIndicatorsProps) {
    const { maxCount, color, spacing, shape, width, height, orientation } = props;
    const indicators = [];

    // Определяем размеры и позиции в зависимости от ориентации
    const isHorizontal = orientation === OrientationEnum.HORIZONTAL;

    // Определим максимальные размеры, доступные для индикаторов
    // В горизонтальной ориентации индикаторы ограничены по высоте высотой объекта
    // В вертикальной ориентации индикаторы ограничены по ширине шириной объекта
    const maxWidth = width; // Оставляем небольшой отступ по бокам
    const maxHeight = height; // Оставляем небольшой отступ сверху и снизу

    // Рассчитаем размер индикатора исходя из доступного пространства
    // и необходимости разместить все индикаторы с учетом отступов между ними
    let itemSize: number;

    if (isHorizontal) {
      // В горизонтальной ориентации размер ограничен либо высотой, либо доступной шириной
      const availableWidth = maxWidth - spacing * (maxCount - 1);
      const maxItemWidth = availableWidth / maxCount;
      // Берем минимальный из доступного размера по ширине и высоте
      itemSize = Math.min(maxItemWidth, maxHeight);
    } else {
      // В вертикальной ориентации размер ограничен либо шириной, либо доступной высотой
      const availableHeight = maxHeight - spacing * (maxCount - 1);
      const maxItemHeight = availableHeight / maxCount;
      // Берем минимальный из доступного размера по ширине и высоте
      itemSize = Math.min(maxItemHeight, maxWidth);
    }

    // Для очень маленьких объектов или большого количества индикаторов
    // мы можем получить отрицательный размер. В этом случае устанавливаем минимальный размер
    itemSize = Math.max(itemSize, 0); // Минимальный размер 5px

    const radius = itemSize / 2;

    // Рассчитаем фактическую ширину, которую займут все индикаторы с учетом отступов
    const totalWidth = isHorizontal ? itemSize * maxCount + spacing * (maxCount - 1) : itemSize;

    const totalHeight = isHorizontal ? itemSize : itemSize * maxCount + spacing * (maxCount - 1);

    // Рассчитаем начальное смещение для центрирования всей группы индикаторов
    const startX = (width - totalWidth) / 2;
    const startY = (height - totalHeight) / 2;

    for (let i = 0; i < maxCount; i++) {
      // Рассчитываем позиции с учетом центрирования
      let posX, posY;

      if (isHorizontal) {
        // В горизонтальной ориентации индикаторы идут слева направо
        posX = startX + i * (itemSize + spacing) + radius - width / 2;
        posY = 0; // По вертикали центрируем
      } else {
        // В вертикальной ориентации индикаторы идут сверху вниз
        posX = 0; // По горизонтали центрируем
        posY = startY + i * (itemSize + spacing) + radius - height / 2;
      }

      let indicator;

      const options = {
        fill: color,
        left: posX,
        top: posY,
        originX: 'center',
        originY: 'center',
        lockScalingX: true,
        lockScalingY: true,
      };

      if (shape === ShapeEnum.CIRCLE) {
        indicator = new fabric.Circle({
          radius,
          ...options,
        });
      } else {
        // ShapeEnum.RECTANGLE
        indicator = new fabric.Rect({
          width: itemSize,
          height: itemSize,
          ...options,
        });
      }

      indicators.push(indicator);
    }

    return indicators;
  }

  create() {
    const { left, top } = elements.getLeftTop(this.canvas);

    const background = this.createRect({
      options: {
        height: 60,
      },
    });

    background.set({
      data: {
        ...background.data,
        id: 'background',
      },
    });

    const data = {
      indicator: 'teamPeriodFouls',
      type: 'composition',
      unground: false,
      teamIndex: 0,
      maxCount: 5,
      shape: ShapeEnum.CIRCLE,
      elementColor: '#FFA500',
      spacing: 0,
      orientation: OrientationEnum.HORIZONTAL,
    };

    // Получаем размеры background
    const backgroundWidth = background.width || 300; // Используем дефолтное значение из BasePlaceholder
    const backgroundHeight = background.height || 60; // Используем дефолтное значение из BasePlaceholder

    // Сначала создаем индикаторы
    const indicators = TeamPeriodFouls.createFoulsIndicators({
      maxCount: data.maxCount,
      color: data.elementColor,
      spacing: data.spacing,
      orientation: data.orientation,
      shape: data.shape,
      width: backgroundWidth,
      height: backgroundHeight,
    });

    // Создаем группу со всеми объектами сразу
    const group = this.createGroup({
      objects: [background],
      data,
      options: {
        left,
        top,
      },
    });

    indicators.forEach((indicator) => {
      group.add(indicator);
    });

    return group;
  }
}

export default TeamPeriodFouls;
